import PropTypes from 'prop-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';

import StarInCircle from '../../images/star-in-circle.svg';
import ChevronDown from '../../images/homepage-refresh/red-triangle-icon-down.svg';
import SectionContainer from '../SectionContainer';
import RichText from '../RichTextV2';
import InlineLink from '../InlineLink';
import { fixPropWarnings } from '../../utils/fixPropWarnings';

const Section = styled.section`
  background-color: ${themeGet(`colors.light-grey`)};
  padding: 40px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 60px 0;
  }
`;

const ListItemHeading = styled.h2.withConfig(fixPropWarnings('variant'))`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading4.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading4.fontSize')};
  line-height: ${themeGet('typography.mobile.heading4.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading4.letterSpacing')};
  text-align: left;

    ${({ variant }) => variant === 'withStarBullets' && css`
      font-size: ${themeGet('typography.mobile.heading2.fontSize')};
      line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
    `}
  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading3.fontSize')};
    line-height: ${themeGet('typography.desktop.heading3.lineHeight')};

    ${({ variant }) => variant === 'withStarBullets' && css`
      font-size: ${themeGet('typography.desktop.heading2.fontSize')};
      line-height: ${themeGet('typography.desktop.heading2.lineHeight')};
      text-align: center;
      display: block;
    `}
  }
`;

const CoverageTitleWrapper = styled.div.withConfig(fixPropWarnings('variant'))`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  ${({ variant }) => variant === 'withStarBullets' && css`
    display: block;
  `}

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 40px;
  }
`;

const OptionalSubCopy = styled.div.withConfig(fixPropWarnings('variant'))`
  margin: 20px 0 10px;
  max-width: auto;

  ${({ variant }) => variant === 'withStarBullets' && css`
    margin: 0;
  `}

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 0 0 0 3rem;
    max-width: 500px;

    ${({ variant }) => variant === 'withStarBullets' && css`
      max-width: none;
      margin: 0;
    `}
  }
`;

const SubHeading = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};
  margin: 0;

  &.subCopyV2 {
    font-size: ${themeGet('typography.mobile.body3.fontSize')};
    line-height: ${themeGet('typography.mobile.body3.lineHeight')};
    padding-top: 10px;
  }
  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body1.fontSize')};
    line-height: ${themeGet('typography.desktop.body1.lineHeight')};

    &.subCopyV2 {
      font-size: ${themeGet('typography.desktop.body2.fontSize')};
      line-height: ${themeGet('typography.desktop.body2.lineHeight')};
      text-align: center;
      padding-top: 20px;
    }
  }
`;

const ElementText = styled.p`
  font-size: 16px;
  line-height: 32px;

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    font-size: 18px;
  }
`;

const StyledAccordion = styled(Accordion)`
  width: 100%;
`;

const StyledAccordionItem = styled(AccordionItem)`
  width: 100%;
  background: ${themeGet('colors.white')};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  margin: 20px 0;
  padding: 20px 20px 10px;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 40px 0;
    padding: 30px 30px 20px;
  }
`;

const AccordionHeading = styled(AccordionItemHeading)`
  width: 100%;
  position: relative;
  cursor: pointer;
  padding: 0 0 10px 0;
`;

const AccordionButton = styled(AccordionItemButton).withConfig(fixPropWarnings('expanded', 'variant'))`
  padding-right: 24px;

  ${({ variant }) => variant === 'withStarBullets' && css`
    &:before {
      background: url(${StarInCircle});
      content: '';
      display: block;
      position: absolute;
      background-size: cover;
      width: 28px;
      height: 27px;
    }
  `}

  p {
    font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
    font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
    font-size: ${themeGet('typography.mobile.body2.fontSize')};
    line-height: ${themeGet('typography.mobile.body2.lineHeight')};
    letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};
    margin: 0;

    ${({ variant }) => variant === 'withStarBullets' && css`
      padding-left: 50px;
    `}
  }

  &:after {
    // eslint-disable-next-line no-extra-boolean-cast
    ${({ expanded }) => (expanded === 'true' ? OpenButtonCss : CollapsedButtonCss)}
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding-right: 50px;

    ${({ variant }) => variant === 'withStarBullets' && css`
      &:before {
        width: 40px;
        height: 40px;
      }
    `}
    p {
      font-weight: ${themeGet('typography.desktop.heading4.fontWeight')};
      font-size: ${themeGet('typography.desktop.heading4.fontSize')};
      line-height: ${themeGet('typography.desktop.heading4.lineHeight')};
      letter-spacing: ${themeGet('typography.desktop.heading4.letterSpacing')};
    }
  }
`;

const ButtonIconCss = css`
  content: url(${ChevronDown});
  display: block;
  position: absolute;
  height: 12px;
  width: 12px;
  right: 0;
  top: 25%;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    height: 15px;
    width: 25px;
  }
`;

const OpenButtonCss = css`
  transform: rotate(-180deg);
  ${ButtonIconCss}
`;

const CollapsedButtonCss = css`
  ${ButtonIconCss}
`;

const AccordionPanel = styled(AccordionItemPanel)`
  border-top: 1px solid #e8e9eb;
  padding: 10px 0;

  p {
    font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
    font-weight: ${themeGet('typography.mobile.body3.fontWeight')};
    font-size: ${themeGet('typography.mobile.body3.fontSize')};
    line-height: ${themeGet('typography.mobile.body3.lineHeight')};
    letter-spacing: ${themeGet('typography.mobile.body3.letterSpacing')};
    margin: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    ${({ variant }) => variant === 'withStarBullets' && css`
      margin-left: 50px;
    `}

    p {
      font-size: ${themeGet('typography.desktop.body2.fontSize')};
      line-height: ${themeGet('typography.desktop.body2.lineHeight')};
    }
  }
`;

const introParagraph = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <SubHeading>{children}</SubHeading>,
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineLink href={node.data.uri}>{children}</InlineLink>
    ),
  },
};

const introParagraphV2 = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <SubHeading className="subCopyV2">{children}</SubHeading>,
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineLink href={node.data.uri}>{children}</InlineLink>
    ),
  },
};

const bodyCopy = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <ElementText>{children}</ElementText>,
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineLink href={node.data.uri}>{children}</InlineLink>
    ),
  },
};

const HomeAutoAccordions = ({ data, variant }) => {
  const {
    headingAndParagraphs,
    sectionParagraphRTF,
    // sectionName,
    sectionId,
    optionalSectionHeading,
  } = data;
  return (
    <Section id={sectionId}>
      <SectionContainer>
        {(optionalSectionHeading || sectionParagraphRTF) && (
          <CoverageTitleWrapper variant={variant}>
            <ListItemHeading variant={variant}>{optionalSectionHeading}</ListItemHeading>
            {sectionParagraphRTF && (
              <OptionalSubCopy variant={variant}>
                <RichText
                  document={sectionParagraphRTF}
                  options={variant === 'withStarBullets' ? introParagraphV2 : introParagraph}
                />
              </OptionalSubCopy>
            )}
          </CoverageTitleWrapper>
        )}
        <StyledAccordion allowMultipleExpanded allowZeroExpanded>
          {headingAndParagraphs.map(
            ({ id, headingText, paragraphs: [{ paragraphRTF }] }) => (
              <StyledAccordionItem key={id}>
                <AccordionHeading>
                  <AccordionItemState>
                    {({ expanded }) => (
                      <AccordionButton variant={variant} expanded={expanded?.toString()}>
                        <p>{headingText}</p>
                      </AccordionButton>
                    )}
                  </AccordionItemState>
                </AccordionHeading>
                <AccordionPanel variant={variant}>
                  <RichText document={paragraphRTF} options={bodyCopy} />
                </AccordionPanel>
              </StyledAccordionItem>
            ),
          )}
        </StyledAccordion>
      </SectionContainer>
    </Section>
  );
};

HomeAutoAccordions.propTypes = {
  data: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool]),
  variant: PropTypes.string,
};

HomeAutoAccordions.defaultProps = {
  data: null,
  variant: null,
};

export default HomeAutoAccordions;
